import { types, flow, getSnapshot, getParent } from 'mobx-state-tree';
import { ApiStore } from './ApiStore';
import { SalesPerformance, NullSalesPerformance } from './SalesPerformance';

const toMaybeString = v => (v == null || typeof v === 'string') ? v : undefined;
const toString = v => (typeof v === 'string' ? v : '');
const USER_TYPES = ['Company Admin', 'Executive', 'Senior Manager', 'Sales Manager', 'Sales Member', 'admin'];
const USER_TYPE_SET = new Set(USER_TYPES);

const normalizeUserType = raw => {
    if (raw == null) return 'Sales Member';
    return USER_TYPE_SET.has(raw) ? raw : 'Sales Member';
};

const _user = types.model({
    userId: types.string,
    firstName: types.string,
    lastName: types.string,
    email: types.string,
    active: types.optional(types.boolean, true),
    userType: types.optional(types.enumeration(USER_TYPES), 'Sales Member'),
    salesPerformance: types.optional(SalesPerformance, getSnapshot(NullSalesPerformance)),
    stripeCustomerId: types.maybe(types.string),
    subscriptionId: types.maybe(types.string),
    reportsToId: types.maybe(types.string),
    disabled: types.maybe(types.boolean),
    companyIds: types.optional(types.array(types.string), []),
    isReadOnly: types.optional(types.boolean, false),
    emailNotificationsEnabled: types.optional(types.boolean, true),
    // Report Sharing preferences (controlled from /profile → Report Sharing card)
    sendMeetingReports: types.optional(types.boolean, true),
    shareWithExternalParticipants: types.optional(types.boolean, false),
    notifyOnExternalShare: types.optional(types.boolean, true),
    hasCompletedTutorial: types.optional(types.boolean, false),
    // Training Plan (guided onboarding) state. Plan *progress* is derived from
    // assessment history (see pagesv2/TrainingPlan/planState.js); these only
    // record what history can't: the welcome modal was shown, the user hid the
    // panel, the plan finished, and which review steps were clicked.
    trainingPlanWelcomeSeenAt: types.maybe(types.string),
    trainingPlanDismissedAt: types.maybe(types.string),
    trainingPlanCompletedAt: types.maybe(types.string),
    trainingPlanChecks: types.optional(types.frozen(), {}),
    // Manager assignment ("redo training"): assignedAt anchors a fresh plan run
    // (planState ignores anything older). assignedBy is server-stamped.
    trainingPlanAssignedAt: types.maybe(types.string),
    trainingPlanAssignedBy: types.maybe(types.string),
    // Optional gender override for Murphy grading. When unset, Murphy continues
    // to infer pronouns from firstName (today's behavior). When set, the grader
    // is given the pronouns explicitly. Values: 'male', 'female', 'they/them'.
    gender: types.maybe(types.enumeration(['male', 'female', 'they/them'])),
    // Opt-in to appear on the company leaderboard. Sales Members/Managers and Senior
    // Managers are always shown; Executives and Company Admins are hidden unless this
    // is true (set from the leaderboard widget's own toggle, a /users/{self} edit).
    leaderboardOptIn: types.optional(types.boolean, false)
}).preProcessSnapshot(snapshot => {
    if (!snapshot) return snapshot;
    return {
        ...snapshot,
        // Required string fields — coerce null/undefined/non-string to '' so a
        // single malformed user row in the API response doesn't kill the
        // entire AdminData[] conversion.
        userId: toString(snapshot.userId),
        firstName: toString(snapshot.firstName),
        lastName: toString(snapshot.lastName),
        email: toString(snapshot.email),
        stripeCustomerId: toMaybeString(snapshot.stripeCustomerId),
        subscriptionId: toMaybeString(snapshot.subscriptionId),
        reportsToId: toMaybeString(snapshot.reportsToId),
        userType: normalizeUserType(snapshot.userType),
        trainingPlanWelcomeSeenAt: toMaybeString(snapshot.trainingPlanWelcomeSeenAt),
        trainingPlanDismissedAt: toMaybeString(snapshot.trainingPlanDismissedAt),
        trainingPlanCompletedAt: toMaybeString(snapshot.trainingPlanCompletedAt),
        trainingPlanAssignedAt: toMaybeString(snapshot.trainingPlanAssignedAt),
        trainingPlanAssignedBy: toMaybeString(snapshot.trainingPlanAssignedBy),
        trainingPlanChecks: (snapshot.trainingPlanChecks && typeof snapshot.trainingPlanChecks === 'object' && !Array.isArray(snapshot.trainingPlanChecks))
            ? snapshot.trainingPlanChecks
            : {},
    };
}).views(self => ({
    get isReadOnlyExecutive() {
        return self.userType === 'Executive' && self.isReadOnly;
    },
    get isReadOnlySalesManager() {
        return self.userType === 'Sales Manager' && self.isReadOnly;
    },
    get isReadOnlySeniorManager() {
        return self.userType === 'Senior Manager' && self.isReadOnly;
    },
    get isExecutive() {
        return self.userType === 'Company Admin' || self.userType === 'Executive' || self.userType === 'admin';
    },
    // Mirrors backend libs/roles.js canManageMembers: the broad-admin tier
    // {admin, Company Admin, Executive} and not read-only (platform admin always).
    // Gates who may add/remove/delete company members in the UI; the server
    // enforces the same rule, so this is UX alignment, not the security boundary.
    get canManageMembers() {
        return self.isAdmin || (self.isExecutive && !self.isReadOnly);
    },
    // Mirrors backend libs/roles.js canEditCompanyContent: who may edit company
    // CONTENT (Company, Offerings, Objection Handling, the Document Library).
    // The inverse of HomeV2's readonly `mode`, plus it also excludes a read-only
    // Company Admin, which that expression does not. UX alignment only; the
    // server enforces the same rule.
    get canEditCompanyContent() {
        if (self.isAdmin) return true;
        const editorTypes = ['Company Admin', 'Executive', 'Senior Manager', 'Sales Manager'];
        return editorTypes.includes(self.userType) && !self.isReadOnly;
    },
    get isSeniorManager() {
        return self.userType === 'Senior Manager';
    },
    get isCompanyAdmin() {
        return self.userType === 'Company Admin';
    },
    get isAdmin() {
        return self.userType === 'admin';
    },
    // canChangeCreditCard getter removed: it read stripeCustomerId, which the
    // backend now strips from company rosters (getUsers.js STRIPPED_FIELDS) —
    // any future use against a roster user would silently always be false.
    get isSalesMember() {
        return self.userType === 'Sales Member';
    },
    get fullName() {
        return `${self.firstName} ${self.lastName}`;
    },
    get hasMoreThanOneCompany() {
        return self.companyIds.length > 1;
    }
}));

export const User = types.compose('User', _user, ApiStore).actions(self => ({
    updateFromSnapshot: flow(function* updateFromSnapshot(snapshot) {
        const patch = {
            firstName: snapshot.firstName,
            lastName: snapshot.lastName,
            userType: snapshot.userType,
            reportsToId: snapshot.reportsToId,
            companyIds: snapshot.companyIds,
            isReadOnly: snapshot.isReadOnly,
            emailNotificationsEnabled: snapshot.emailNotificationsEnabled,
            sendMeetingReports: snapshot.sendMeetingReports,
            shareWithExternalParticipants: snapshot.shareWithExternalParticipants,
            notifyOnExternalShare: snapshot.notifyOnExternalShare,
            // Send explicit null when cleared so patchHandler REMOVEs the attribute;
            // undefined gets dropped by JSON.stringify and the field would persist forever.
            gender: snapshot.gender ?? null
            // leaderboardOptIn deliberately NOT sent: setLeaderboardOptIn owns
            // it (see comment below). Re-sending `?? false` here meant any
            // full-profile save from a stale/roster instance silently reset a
            // member's opt-in — the exact lost-update that setter exists to avoid.
        };

        // Only include active if it actually changed to avoid triggering spurious activation emails
        if (snapshot.active !== self.active) {
            patch.active = snapshot.active;
        }

        yield self.patchApi(`/users/${self.userId ?? snapshot.userId}`, patch, self);
    }),
    // Targeted opt-in write — PATCHes ONLY leaderboardOptIn so toggling
    // leaderboard visibility never re-writes role / companyIds / reportsToId /
    // gender (avoids the full-profile re-send that updateFromSnapshot does and
    // the lost-update race that comes with it).
    setLeaderboardOptIn: flow(function* setLeaderboardOptIn(value) {
        yield self.patchApi(`/users/${self.userId}`, { leaderboardOptIn: value }, self);
    }),
    // Targeted Report Sharing write (same rationale): ReportSharingPanel and
    // the profile form share one editUser node with per-mount snapshots — a
    // full-snapshot save from either silently reverted the other's changes.
    setReportSharing: flow(function* setReportSharing({ sendMeetingReports, shareWithExternalParticipants, notifyOnExternalShare }) {
        yield self.patchApi(`/users/${self.userId}`, {
            sendMeetingReports,
            shareWithExternalParticipants,
            notifyOnExternalShare,
        }, self);
    }),
    createFromSnapshot: flow(function* createFromSnapshot(snapshot) {
        snapshot.active = true;
        yield self.postApi('/users', snapshot, self);
    }),
    // `reportsToId` is optional: their existing manager may not be a member of
    // the company they are being added to, which leaves a blank "Reports To" in
    // that company's grid and hides them from every manager's team view.
    // Both actions PATCH first and mutate local state only on success —
    // mutating before the write left phantom memberships in the local model
    // after a 403/network failure, which every later save then re-sent.
    addUserToCompany: flow(function* addUserToCompany(companyId, reportsToId) {
        const snapshot = getSnapshot(self);
        yield self.updateFromSnapshot({
            ...snapshot,
            companyIds: [...snapshot.companyIds, companyId],
            reportsToId: reportsToId || snapshot.reportsToId,
        });
    }),
    removeUserFromCompany: flow(function* removeUserFromCompany(companyId) {
        const snapshot = getSnapshot(self);
        yield self.updateFromSnapshot({
            ...snapshot,
            companyIds: snapshot.companyIds.filter(id => id !== companyId),
        });
    }),
    delete: flow(function* deleteUser() {
        yield self.deleteApi(`/users/${self.userId}`);
    }),
    setHasCompletedTutorial(value) {
        self.hasCompletedTutorial = value;
    },
    // Training Plan writes are targeted single-field PATCHes (same rationale as
    // setLeaderboardOptIn): they must never re-send role / companyIds / gender.
    setTrainingPlanWelcomeSeen: flow(function* setTrainingPlanWelcomeSeen() {
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanWelcomeSeenAt: new Date().toISOString() }, self);
    }),
    setTrainingPlanDismissed: flow(function* setTrainingPlanDismissed() {
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanDismissedAt: new Date().toISOString() }, self);
    }),
    // Settings-page "Resume Training Plan": null = patchHandler REMOVE, so the
    // dismissal is fully cleared and the dashboard panel returns.
    clearTrainingPlanDismissed: flow(function* clearTrainingPlanDismissed() {
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanDismissedAt: null }, self);
        self.trainingPlanDismissedAt = undefined;
    }),
    setTrainingPlanCompleted: flow(function* setTrainingPlanCompleted() {
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanCompletedAt: new Date().toISOString() }, self);
    }),
    markTrainingPlanCheck: flow(function* markTrainingPlanCheck(checkId) {
        if (!checkId) return;
        // Idempotent WITHIN THE CURRENT RUN only. A check left over from a prior
        // run has a timestamp older than the assignment anchor — planState treats
        // it as un-done (afterAnchor), so the write must be allowed to refresh it,
        // otherwise a manager-restarted plan can never re-tick a review step.
        const existing = self.trainingPlanChecks && self.trainingPlanChecks[checkId];
        const anchor = self.trainingPlanAssignedAt;
        if (existing && (!anchor || new Date(existing) >= new Date(anchor))) return;
        const next = { ...(self.trainingPlanChecks || {}), [checkId]: new Date().toISOString() };
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanChecks: next }, self);
    }),
    // Manager assignment — called on a ROSTER instance (the target user), not
    // currentUser. The server stamps trainingPlanAssignedBy from the caller and
    // enforces the assigner matrix (exec/CA company-wide; managers their tree).
    assignTrainingPlan: flow(function* assignTrainingPlan() {
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanAssignedAt: new Date().toISOString() }, self);
    }),
    unassignTrainingPlan: flow(function* unassignTrainingPlan() {
        yield self.patchApi(`/users/${self.userId}`, { trainingPlanAssignedAt: null, trainingPlanAssignedBy: null }, self);
    }),
}));

export const NullUser = User.create(
    {
        userId: '',
        firstName: '',
        lastName: '',
        companyIds: [],
        email: '',
        active: true,
        userType: 'Company Admin'
    }
);
